/**
 * This is a component for displaying items in a grid. The item will be rendered with
 * the childComponent passed in as a prop.
 *
 * @module components/Grid
 */

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { AriaAttributes, AriaRoles } from '../../constants/AccessibilityConstants';
import GridConstants from '../../constants/Grid';

import './Grid.scss';

// when adding/deleting items from grid, we only want to animate as many items as the user will see, for performance
const MAX_GRID_ITEMS_VIEWABLE = 18;

export class Grid extends React.Component {
  /**
   * @property {Array|List} itemProps - an array or Immutable List of items that will be used to populate the grid.
   * The size of the grid will be equal to the size of the itemProps.
   * @property {Node} children - something to be rendered as a header for the grid.
   * @property {number} activeIndex - the index of the grid element that was clicked on (used for animation or styling). Can be null
   */
  static propTypes = {
    items: PropTypes.oneOfType([PropTypes.array, ImmutablePropTypes.list]).isRequired,
    children: PropTypes.node,
    activeIndex: PropTypes.number,
  };

  render() {
    const accessMenu = {
      role: [AriaRoles.MENU],
      [AriaAttributes.LABELLEDBY]: 'grid_header_label',
    };
    const accessMenuItem = {
      role: [AriaRoles.MENUITEM],
      tabIndex: '-1',
    };
    const accessPlaceholder = {
      role: [AriaRoles.PRESENTATION],
      [AriaAttributes.HIDDEN]: 'true',
    };
    const items = this.props.items.map((item, index) => {
      // 'animate' and 'clicked' classes should not be applied if active index is null
      const itemClasses = classNames({
        Grid__item: true,
        'Grid__item--clicked': index === this.props.activeIndex,
        'Grid__item--animate':
          index > this.props.activeIndex &&
          index < this.props.activeIndex + MAX_GRID_ITEMS_VIEWABLE,
      });
      return (
        <li
          className={itemClasses}
          id={`grid_item_${index}`}
          key={`item${index}`}
          {...accessMenuItem}
        >
          {item}
        </li>
      );
    });
    // These placeholders exist to allow us to use flexbox space-around to
    // justify content and still have our last row look left aligned if it isn't
    // full
    const placeholders = Array.from(Array(GridConstants.NUM_PLACEHOLDERS)).map((_, i) => (
      <li className='Grid__placeholder' key={`placeholder${i}`} {...accessPlaceholder}></li>
    ));
    return (
      <div className='Grid'>
        <div className='Grid__header'>
          <h1 id='grid_header_label' className='Grid__header__label'>
            {' '}
            Select a Station{' '}
          </h1>
          {this.props.children}
        </div>
        <ul className='Grid__content' {...accessMenu}>
          {items.concat(placeholders)}
        </ul>
      </div>
    );
  }
}

export default Grid;
